'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { textToSpeechApi } from 'src/api/text-to-speech/text-to-speech.api'

type UseArticleReadAloudStreamingParams = {
  articleId: string
  text: string
}

function supportsMediaSourceMpeg() {
  return (
    typeof MediaSource !== 'undefined' &&
    MediaSource.isTypeSupported('audio/mpeg')
  )
}

export function useArticleReadAloudStreaming({
  articleId,
  text,
}: UseArticleReadAloudStreamingParams) {
  const [isLoading, setIsLoading] = useState(false)
  const [isBuffering, setIsBuffering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioSrc, setAudioSrc] = useState<string | null>(null)

  const mediaSourceRef = useRef<MediaSource | null>(null)
  const sourceBufferRef = useRef<SourceBuffer | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const audioUrlRef = useRef<string | null>(null)
  const streamChunksRef = useRef<Uint8Array[]>([])

  /**
   * Cleanup streaming resources and reset loading/buffering state
   */
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    mediaSourceRef.current = null
    sourceBufferRef.current = null

    setIsLoading(false)
    setIsBuffering(false)

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
    streamChunksRef.current = []
    setAudioSrc(null)
  }, [])

  /**
   * Stop audio + cleanup
   */
  const handleClose = useCallback(() => {
    cleanup()
  }, [cleanup])

  /**
   * Start streaming TTS
   */
  const handleListen = useCallback(() => {
    const trimmedText = text.trim()
    if (!trimmedText) return

    cleanup()

    streamChunksRef.current = []
    setIsLoading(true)
    setError(null)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    if (!supportsMediaSourceMpeg()) {
      void (async () => {
        try {
          const blob = await textToSpeechApi.synthesize(
            { id: articleId, text: trimmedText },
            abortController.signal
          )

          if (abortController.signal.aborted) return

          const blobUrl = URL.createObjectURL(blob)
          audioUrlRef.current = blobUrl
          setAudioSrc(blobUrl)
        } catch (err) {
          if (abortController.signal.aborted) return

          console.error('[ArticleReadAloud] Fetch error:', err)
          setError(err instanceof Error ? err.message : 'Failed to load audio')
        } finally {
          setIsLoading(false)
          setIsBuffering(false)
        }
      })()

      return
    }

    try {
      // cleanup() already revoked any previous URL
      const mediaSource = new MediaSource()
      mediaSourceRef.current = mediaSource

      const url = URL.createObjectURL(mediaSource)
      audioUrlRef.current = url

      setAudioSrc(url)

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      mediaSource.addEventListener('sourceopen', async () => {
        try {
          const mimeType = 'audio/mpeg'

          if (!MediaSource.isTypeSupported(mimeType)) {
            throw new Error('Audio format not supported')
          }

          const sourceBuffer = mediaSource.addSourceBuffer(mimeType)
          sourceBufferRef.current = sourceBuffer

          setIsLoading(false)
          setIsBuffering(true)

          await textToSpeechApi.synthesizeStream(
            { id: articleId, text: trimmedText },
            {
              onStreamStart: () => {},

              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onStream: async (chunk: Uint8Array) => {
                streamChunksRef.current.push(chunk)
                const buffer = sourceBufferRef.current
                if (!buffer) return

                await new Promise<void>((resolve) => {
                  const appendChunk = () => {
                    if (!buffer.updating) {
                      buffer.appendBuffer(chunk as unknown as BufferSource)
                      resolve()
                    } else {
                      buffer.addEventListener('updateend', appendChunk, {
                        once: true,
                      })
                    }
                  }

                  appendChunk()
                })
              },

              onStreamEnd: () => {
                setIsBuffering(false)

                const ms = mediaSourceRef.current
                const chunks = streamChunksRef.current
                if (chunks.length > 0) {
                  const blob = new Blob(chunks as BlobPart[], {
                    type: 'audio/mpeg',
                  })
                  const blobUrl = URL.createObjectURL(blob)
                  if (audioUrlRef.current) {
                    URL.revokeObjectURL(audioUrlRef.current)
                  }
                  audioUrlRef.current = blobUrl
                  setAudioSrc(blobUrl)
                  mediaSourceRef.current = null
                  sourceBufferRef.current = null
                  try {
                    ms?.endOfStream()
                  } catch {
                    // ignore
                  }
                } else if (ms && ms.readyState === 'open') {
                  try {
                    ms.endOfStream()
                  } catch {
                    // ignore
                  }
                }
              },

              onStreamAbort: () => {
                setIsBuffering(false)
              },
            },
            abortController.signal
          )
        } catch (err) {
          console.error('Streaming error:', err)
          setError(err instanceof Error ? err.message : 'Streaming failed')
          setIsBuffering(false)
        }
      })
    } catch (err) {
      if (abortController.signal.aborted) return

      console.error('[ArticleReadAloud] Fetch error:', err)

      setError(err instanceof Error ? err.message : 'Failed to load audio')
      setIsLoading(false)
      setIsBuffering(false)
    }
  }, [articleId, text, cleanup])

  /**
   * Cleanup when component unmounts
   */
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  const isBusy = isLoading || isBuffering

  return {
    audioSrc,
    isLoading: isLoading && !isBuffering,
    isBuffering,
    isBusy,
    error,
    handleListen,
    handleClose,
  }
}
